import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface SocialChannel {
  id: string;
  name: string;
  followers: number;
  engagement: number;
  posts_month: number;
  growth: string;
}

export interface SocialPost {
  id: string;
  channel: string;
  content: string;
  status: string;
  scheduled_at: string;
  likes: number;
  comments: number;
  shares: number;
  created_at: string;
}

export function useSocialData() {
  const [channels, setChannels] = useState<SocialChannel[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, pRes] = await Promise.all([
        fetch(`${API_URL}/api/social/channels`, { credentials: "include" }),
        fetch(`${API_URL}/api/social/posts`, { credentials: "include" }),
      ]);
      if (chRes.ok) setChannels((await chRes.json()).channels || []);
      if (pRes.ok) setPosts((await pRes.json()).posts || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createPost = async (post: Partial<SocialPost>) => {
    try {
      const res = await fetch(`${API_URL}/api/social/posts`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(post),
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => [data, ...prev]);
        toast.success("Publication créée !");
        return data;
      }
    } catch { toast.error("Erreur"); }
    return null;
  };

  const deletePost = async (id: string) => {
    await fetch(`${API_URL}/api/social/posts/${id}`, { method: "DELETE", credentials: "include" });
    setPosts(prev => prev.filter(p => p.id !== id));
    toast.success("Publication supprimée");
  };

  const updateChannel = async (id: string, updates: Partial<SocialChannel>) => {
    await fetch(`${API_URL}/api/social/channels/${id}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    setChannels(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  return { channels, posts, loading, createPost, deletePost, updateChannel, refresh: fetchAll };
}
